/* SiSweets brand motion.
   Scroll reveals + per-word heading rise.
   IntersectionObserver only — no GSAP, no Lenis, no CDN.
   ponytail: IO covers reveal-on-scroll everywhere including iOS Safari.
   Add Lenis (~3kb) only if inertial smooth-scroll is specifically wanted. */

(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Split marked headings into per-word spans so they can rise in sequence.
  const split = (el) => {
    if (el.dataset.sisSplitDone) return;
    el.dataset.sisSplitDone = '1';
    const words = el.textContent.trim().split(/\s+/);
    el.textContent = '';
    words.forEach((word, i) => {
      const outer = document.createElement('span');
      outer.className = 'sis-word';
      const inner = document.createElement('span');
      inner.textContent = word;
      inner.style.setProperty('--sis-delay', `${i * 55}ms`);
      outer.append(inner, document.createTextNode(' '));
      el.append(outer);
    });
  };

  const init = () => {
    const targets = document.querySelectorAll('[data-sis-reveal], [data-sis-split]');
    if (!targets.length) return;

    if (reduce) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    targets.forEach((el) => {
      if (el.hasAttribute('data-sis-split')) split(el);
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );

    targets.forEach((el) => io.observe(el));
  };

  // Spring the cart bubble whenever its number changes. The drawer replaces
  // the node wholesale, so watch the subtree rather than one element.
  const cartBump = () => {
    if (reduce) return;
    let last = null;
    const read = () => document.querySelector('.cart-bubble__text-count')?.textContent.trim() ?? null;
    last = read();

    new MutationObserver(() => {
      const now = read();
      if (now === null || now === last) return;
      last = now;
      document.querySelectorAll('.cart-bubble').forEach((b) => {
        b.classList.remove('is-bumped');
        void b.offsetWidth; // restart the animation
        b.classList.add('is-bumped');
      });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  // Pressing "−" at quantity 1 should remove the line, but the input has
  // min="1" so the stepper stops there. Rather than re-implementing a cart
  // mutation, hand off to the theme's own remove button for that row — it
  // already handles the request, the row animation and the re-render.
  // Capture phase so this runs before the theme's own click handler.
  const minusRemoves = () => {
    // The theme disables "−" once quantity reaches 1, so no click ever fires.
    // Re-enable it on any row that has a remove button to hand off to, and
    // re-apply after every cart re-render (the theme rebuilds these nodes).
    const enableMinus = () => {
      document.querySelectorAll('.cart-items__table-row').forEach((row) => {
        const input = row.querySelector('input[name="updates[]"]');
        const minus = row.querySelector('button[name="minus"]');
        const remove = row.querySelector('.cart-items__remove');
        if (!input || !minus || !remove) return;
        if (Number(input.value) !== 1) return;
        if (!minus.disabled) return;
        minus.disabled = false;
        minus.dataset.sisRemovesLine = '1';
      });
    };

    enableMinus();
    const host = document.querySelector('cart-items-component') || document.body;
    new MutationObserver(enableMinus).observe(host, { childList: true, subtree: true });

    document.addEventListener('click', (event) => {
      const minus = event.target.closest?.('button[name="minus"]');
      if (!minus) return;

      const row = minus.closest('.cart-items__table-row, tr, li');
      const input = row?.querySelector('input[name="updates[]"]');
      const remove = row?.querySelector('.cart-items__remove');
      if (!input || !remove || remove.disabled) return;
      if (Number(input.value) > 1) return;

      event.preventDefault();
      event.stopPropagation();
      remove.click();
    }, true);
  };

  // Quantity stepper on product cards. Each card's add-to-cart form already
  // carries a hidden quantity input (defaulting to 1), so this just drives
  // that value — no interception of the theme's submit handler needed.
  const cardQuantity = () => {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.dataset.sisQty) return;

      const hidden = form.querySelector('input[name="quantity"]');
      const addBtn = form.querySelector('button[name="add"]');
      if (!hidden || !addBtn) return;

      // Skip the product page's own form — it already shows a stepper.
      const existing = form.querySelector('.quantity-selector, quantity-selector-component');
      if (existing && existing.getBoundingClientRect().width > 0) return;
      if (hidden.type !== 'hidden') return;

      form.dataset.sisQty = '1';

      const row = document.createElement('div');
      row.className = 'sis-buy';

      const wrap = document.createElement('div');
      wrap.className = 'sis-qty';
      wrap.innerHTML =
        '<button type="button" class="sis-qty__btn" data-step="-1" aria-label="Minder">&minus;</button>' +
        '<output class="sis-qty__value" aria-live="polite">1</output>' +
        '<button type="button" class="sis-qty__btn" data-step="1" aria-label="Meer">+</button>';

      // The theme's own add button is a bare icon inside .quick-add, an
      // absolutely-positioned overlay on the image revealed only by
      // product-card:hover. Nobody on a touch screen can reach it and nobody
      // who doesn't already know it's there will hover to find it, so the
      // card reads as having no way to buy. Give every card a plain labelled
      // button in normal flow that forwards the click to the real one --
      // that keeps the theme's cart drawer, loading and error handling.
      const buy = document.createElement('button');
      buy.type = 'button';
      buy.className = 'sis-buy__btn';
      buy.textContent = 'In winkelwagen';
      buy.addEventListener('click', () => addBtn.click());

      const value = wrap.querySelector('.sis-qty__value');
      const render = () => {
        value.textContent = hidden.value;
        wrap.querySelector('[data-step="-1"]').disabled = Number(hidden.value) <= 1;
      };

      wrap.addEventListener('click', (event) => {
        const step = event.target.closest('.sis-qty__btn');
        if (!step) return;
        event.preventDefault();
        const next = Math.max(1, Math.min(99, Number(hidden.value) + Number(step.dataset.step)));
        hidden.value = String(next);
        render();
      });

      render();

      // The add button sits in an absolutely-positioned overlay on the
      // product image, so anything inserted beside it lands on top of it.
      // Mount into the card's normal content flow instead, after the price.
      row.append(wrap, buy);

      const content = form.closest('product-card')?.querySelector('.product-card__content');
      if (content) {
        content.append(row);
      } else {
        addBtn.parentElement.insertBefore(row, addBtn);
      }
    });
  };

  const boot = () => {
    init(); cartBump(); minusRemoves(); cardQuantity();
    // Product lists re-render on filter/paginate, so re-attach then.
    new MutationObserver(cardQuantity).observe(document.body, { childList: true, subtree: true });
  };

  document.addEventListener('DOMContentLoaded', boot, { once: true });
  // Re-run reveals when the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', init);
})();
